import React, { useState, useEffect } from 'react';
import * as d3 from 'd3';
import './radarGraph.css';

const RadarChart = ({
  data,
  categories = ['Policy', 'People', 'Technology'],
  levels = 8,
  size
}) => {
  const angleSlice = (Math.PI * 2) / categories.length;
  const angleSliceLabels = (Math.PI * 2) / 18;
  const [radarSize, setRadarSize] = useState(undefined);
  const [radius, setRadius] = useState(undefined);
  // const [categoryScales, setCategoryScales] = useState(undefined);
  const [triangles, setTriangles] = useState([{key:'median', d:undefined}, {key:'target', d:undefined}]);
  const categoryRotations = ['rotate(-6.5deg)','rotate(95deg)','rotate(187.5deg)']
  const levelLabels = [
    {
      label: "Leading",
      slice: 1,
      textFill: '#000',
      levels: [8,7],
      circleFill: '#fff'
    },
    {
      label: 'Advancing',
      slice: 2,
      textFill: '#002c5f',
      levels: [6,5],
      circleFill: '#e4e9f1'
    },
    {
      label: 'Developing',
      slice: 3,
      textFill: '#37848f',
      levels: [4,3],
      circleFill: '#e9f1f4'
    },
    {
      label: 'Exploring',
      slice: 4,
      textFill: '#8e4585',
      levels: [2,1],
      circleFill: '#eddde9'
    }
  ];
    function getCoordinate(d, i, axis, start) {
      const angle = angleSliceLabels * d.slice - Math.PI / 2;
      if (start) {
        if (axis === "x") {
          return Math.cos(angle) * radius + (20 - (5*i));
        } else if (axis === "y") {
          return Math.sin(angle) * radius - 6 + (1 * i);
        } else {
          return undefined
        }
      } else {
        const r = (radius / levels) * d.levels[0]
        if (axis === "x") {
          return Math.cos(angle) * r;        
        } else if (axis === "y") {
          return Math.sin(angle) * r;        
        } else {
          return undefined
        }
      }
    }
    useEffect(()=> {
      if (size?.width > 0) { 
        setRadarSize(Math.min(size.width, size.height)) 
      }  
    },[size?.width, size?.height]);
    useEffect(() => {
      if (radarSize) { setRadius(radarSize / 2 - 60) }
    }, [radarSize, size])
    useEffect(() => {
      if (radius){
        const newTriangles = triangles.map(t => {
          const d = d3.lineRadial()
          .radius((d, i) => {
            const cat = data.max.find(f => f.axis === d.axis)
            const scale = d3.scaleLinear().domain([0, cat.value]).range([0, radius])
            return scale(d.value)
          })
          .angle((d, i) => i * angleSlice)
          .curve(d3.curveLinearClosed)(data[t.key])
          return {key: t.key, d: d}
        })
        setTriangles(newTriangles);
      }
        
    },[radius, data, angleSlice, size])
    if (radarSize && radarSize > 0) {
      return <svg width={radarSize} height={radarSize-45} style={{margin: 'auto'}} xmlns="http://www.w3.org/2000/svg" >
    <defs>
      <marker 
        id="triangle" 
        viewBox="0 0 100 100" 
        refX="50" 
        refY="60" 
        markerUnits="strokeWidth" 
        markerWidth="10" 
        markerHeight="20" 
        orient="auto">
        <path 
          fill="context-fill" 
          stroke="context-stroke" 
          d="M1.95,111.2c-2.65,2.72-2.59,7.08,0.14,9.73c2.72,2.65,7.08,2.59,9.73-0.14L64.94,66l-4.93-4.79l4.95,4.8 c2.65-2.74,2.59-7.11-0.15-9.76c-0.08-0.08-0.16-0.15-0.24-0.22L11.81,2.09c-2.65-2.73-7-2.79-9.73-0.14 C-0.64,4.6-0.7,8.95,1.95,11.68l48.46,49.55L1.95,111.2L1.95,111.2L1.95,111.2z">
        </path>
      </marker>
      {radius && categories.map((category,i) => {
        return <path
          key={category}
          fill='none'
          stroke='red'
          style={{transform: categoryRotations[i]}}
          id={category}
          d={d3.arc()
            .innerRadius(0) // For a full circle, inner radius is 0
            .outerRadius(radius) // Set the desired radius of the circle
            .startAngle(i => angleSlice * i * 2 * Math.PI)
            .endAngle(angleSlice)(i) // A full circle is 2 * PI radians
            }
        ></path>
      })}
    </defs>
    {radius && 
    <g id="main" transform={`translate(${radarSize / 2 - 20 },${radarSize / 2})`}>
      {levelLabels.map(label => {
        return label.levels.map(level => {
          return <circle
              key={`${label.label}${level}`}
              r={(radius / levels) * level}
              fill={label.circleFill}
              stroke={level === levels ? '#000' : '#ccc'} 
              strokeDasharray={level === levels ? "" : "3 2"}
              strokeWidth={level === levels ? .5 : 1} >
          </circle>
        })
      })}
      {levelLabels.map((label,i) => {
        return <g key={`${label.label}${i}`}>
          <text
            x={getCoordinate(label, i, "x", true)}
            y={getCoordinate(label, i, "y", true)}
            fill={label.textFill}
            fontSize={12}
            textAnchor='start'
            dominantBaseline='middle'
            dx={4}
          >{label.label}</text>
          <line
            x1={getCoordinate(label, i, "x", true)}
            x2={getCoordinate(label, i, "x", false)}
            y1={getCoordinate(label, i, "y", true)}
            y2={getCoordinate(label, i, "y", false)}
            markerEnd="url(#triangle)"
            stroke={label.textFill}
            fill={label.textFill}
          ></line>
        </g> 
      })}
      {categories.map((category,i) => {
        const angle = angleSlice * i - Math.PI / 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        return <g key={category}>
          <line
            x1={0}
            y1={0}
            x2={x}
            y2={y}
            stroke='#ccc'
            strokeDasharray="2,2"
          ></line>
          <text
            dy={category === "Policy" ? -10 : 18}
          >
            <textPath
              href={`#${category}`}
              fill='#002c5f'
            >{category}</textPath>
          </text>
        </g>
      })}
      {data.loaded && triangles.map((t) => {
        return <path
          key={t.key}
          d={t.d}
          fill={t.key === 'target' ? '#A15A95' : 'none'}
          stroke={t.key === 'target' ? '#A15A95' : '#37848f'}
          fillOpacity={t.key === 'target' ? 0.4 : 0.0}
          strokeWidth={t.key === 'target' ? 0 : 2}
        ></path>
      })}
    </g>
    }
    </svg>
    } else {
      return null
    }
};

export default RadarChart;
